
import React from 'react';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

const buttonClasses = cva(
  'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: 'hover:opacity-90 focus:ring-red-500',
        secondary: 'bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-500',
        outline: 'border-2 bg-transparent hover:bg-opacity-10 focus:ring-red-500',
      },
      size: {
        small: 'text-sm px-3 py-1.5',
        medium: 'text-base px-4 py-2',
        large: 'text-lg px-6 py-3',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  }
);

const Button = ({
  // Required parameters with defaults
  text = "Join Waitlist",
  text_font_size = "16",
  text_font_family = "DM Sans",
  text_font_weight = "500",
  text_line_height = "21px",
  text_text_align = "center",
  border_border_radius = "8px",
  text_color = "#ffffff",
  fill_background = "linear-gradient(180deg, #FF3333 0%, #FF3333 100%)",
  
  // Optional parameters (no defaults)
  text_text_transform,
  text_background,
  fill_background_color,
  effect_box_shadow,
  layout_width,
  padding,
  position,
  
  // Standard React props
  variant,
  size,
  disabled = false,
  className,
  children,
  onClick,
  type = "button",
  ...props
}) => {
  // Safe validation for optional parameters
  const hasValidTextTransform = text_text_transform && typeof text_text_transform === 'string' && text_text_transform?.trim() !== '';
  const hasValidTextBackground = text_background && typeof text_background === 'string' && text_background?.trim() !== '';
  const hasValidFillBackgroundColor = fill_background_color && typeof fill_background_color === 'string' && fill_background_color?.trim() !== '';
  const hasValidBoxShadow = effect_box_shadow && typeof effect_box_shadow === 'string' && effect_box_shadow?.trim() !== '';
  const hasValidWidth = layout_width && typeof layout_width === 'string' && layout_width?.trim() !== '';
  const hasValidPadding = padding && typeof padding === 'string' && padding?.trim() !== '';
  const hasValidPosition = position && typeof position === 'string' && position?.trim() !== '';

  // Build optional Tailwind classes
  const optionalClasses = [
    hasValidTextTransform ? `${text_text_transform}` : '',
    // Don't use shadow-[${effect_box_shadow}] as it causes CSS parsing errors
    // Use inline style instead (already in buttonStyles)
    hasValidWidth ? `w-[${layout_width}]` : '',
    hasValidPadding ? `p-[${padding}]` : '',
    hasValidPosition ? position : '',
  ]?.filter(Boolean)?.join(' ');

  // Build inline styles for required parameters
  const buttonStyles = {
    fontSize: text_font_size ? `${text_font_size}px` : '16px',
    fontFamily: text_font_family || 'DM Sans',
    fontWeight: text_font_weight || '500',
    lineHeight: text_line_height || '21px',
    textAlign: text_text_align || 'center',
    color: text_color || '#ffffff',
    background: hasValidFillBackgroundColor ? fill_background_color : (fill_background || 'linear-gradient(180deg, #FF3333 0%, #FF3333 100%)'),
    borderRadius: border_border_radius || '8px',
    boxShadow: hasValidBoxShadow ? effect_box_shadow : undefined,
    ...(hasValidTextBackground && { backgroundColor: text_background }),
  };

  // Safe click handler
  const handleClick = (event) => {
    if (disabled) return;
    if (typeof onClick === 'function') {
      onClick(event);
    }
  };

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={handleClick}
      style={buttonStyles}
      className={twMerge(
        buttonClasses({ variant, size }),
        optionalClasses,
        className
      )}
      aria-disabled={disabled}
      {...props}
    >
      {children || text}
    </button>
  );
};

export default Button;